/**
 * @fileoverview Reusable field mapping editor for the Dataset Builder wizard and detail slide-in.
 * @module system-web/components/migration/FieldMappingEditor
 *
 * Responsibilities:
 * - Render the editable list of FieldMapping[] rows.
 * - Allow admins to add a new source column → target collection.field pairing.
 * - Allow admins to remove a mapping row.
 * - Allow admins to set the optional type transform per row.
 * - Highlight unmapped required target fields as errors (blocks wizard advance).
 * - Highlight type-mismatch warnings for mismatched transform selections.
 * - Pre-populate rows from AI-suggested mappings when provided.
 *
 * Dependencies:
 * - MigrationDatasetTemplate types from @firesquirrel/legacy-migration.
 * - lucide-react icons.
 *
 * Architecture:
 * - Presentation + local state only.
 * - Receives available source columns and target collections from props (supplied by wizard/parent).
 * - Calls onMappingsChange on every edit — parent holds authoritative fieldMappings[].
 * - No Dexie reads; no API calls.
 *
 * Notes:
 * - The `requiredFields` list for each target collection is provided via the `targetCollections` prop.
 * - A mapping row is "unmapped-required" if a requiredField for the selected targetCollection
 *   has no corresponding FieldMapping. This state triggers an error chip.
 * - Reused in both DatasetBuilderWizard (step 7) and DatasetDetailSlideIn (Mappings tab view).
 *
 * @reusable
 * Usage: Used in DatasetBuilderWizard step 7 (editable) and DatasetDetailSlideIn mappings view (read-only).
 * Rules: stateful — controlled via fieldMappings + onMappingsChange; read-only mode via readOnly prop.
 *
 * @localFirst
 * Source of Truth: wizard/parent state (derived from MigrationDatasetTemplate.fieldMappings).
 * Write Flow: onMappingsChange callback propagates to parent; parent posts to server on save.
 * Sync Strategy: No Dexie access — this component is purely a controlled form editor.
 * Conflict Resolution: N/A — component state is ephemeral until parent saves.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Trash2, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';
import type { FieldMapping, SuggestedMapping } from '../../types/legacyMigrationTypes';

// ---------------------------------------------------------------------------
// Supporting types
// ---------------------------------------------------------------------------

/**
 * @contract SourceTableColumns
 *
 * Purpose: Describes the available columns on a single source SQL table.
 * Fields:
 * - tableName: Qualified table name (e.g. 'dbo.viol_codeset').
 * - columns: Column names available for mapping.
 * Rules: tableName must appear in the template source.primaryTable or source.childTables.
 * Sync: Provided by wizard parent from legacySchemaManifestService.
 */
export interface SourceTableColumns {
  tableName: string;
  columns: string[];
}

/**
 * @contract TargetCollectionFields
 *
 * Purpose: Describes the available fields on a target MongoDB collection.
 * Fields:
 * - collectionName: FireSight MongoDB collection name (from knownCollectionsRegistry).
 * - requiredFields: Fields that must be mapped; absence blocks save.
 * - nullableFields: Fields that may be unmapped without error.
 * Rules: collectionName must be from the knownCollectionsRegistry.
 * Sync: Provided by wizard parent from getAllCollections() (knownCollectionsRegistry).
 */
export interface TargetCollectionFields {
  collectionName: string;
  requiredFields: string[];
  nullableFields: string[];
}

/**
 * @contract FieldMappingEditorProps
 *
 * Purpose: Props for the FieldMappingEditor component.
 * Fields:
 * - fieldMappings: Controlled array of current FieldMapping rows.
 * - onMappingsChange: Called on every add/remove/update; parent updates its state.
 * - sourceTables: Available source tables and their columns.
 * - targetCollections: Available target collections and their field lists.
 * - suggestedMappings: AI-suggested mappings to pre-populate (wizard step 7 only).
 * - readOnly: When true, renders a view-only table without add/remove controls.
 * Rules:
 * - fieldMappings must be a stable reference (parent-controlled).
 * - readOnly defaults to false.
 * Sync: fieldMappings synced to parent state, not directly to Dexie.
 */
export interface FieldMappingEditorProps {
  fieldMappings: FieldMapping[];
  onMappingsChange: (mappings: FieldMapping[]) => void;
  sourceTables: SourceTableColumns[];
  targetCollections: TargetCollectionFields[];
  suggestedMappings?: SuggestedMapping[];
  readOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Transform options
// ---------------------------------------------------------------------------

const TRANSFORM_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'boolean', label: 'boolean' },
  { value: 'date', label: 'date' },
  { value: 'trim', label: 'trim' },
  { value: 'uppercase', label: 'uppercase' },
];

// ---------------------------------------------------------------------------
// New mapping row form (collapsed into an "Add mapping" row at the bottom)
// ---------------------------------------------------------------------------

interface AddMappingRowProps {
  sourceTables: SourceTableColumns[];
  targetCollections: TargetCollectionFields[];
  onAdd: (mapping: FieldMapping) => void;
}

function AddMappingRow({ sourceTables, targetCollections, onAdd }: AddMappingRowProps) {
  const [sourceTable, setSourceTable] = useState('');
  const [sourceColumn, setSourceColumn] = useState('');
  const [targetCollection, setTargetCollection] = useState('');
  const [targetField, setTargetField] = useState('');
  const [transform, setTransform] = useState('');

  const sourceColumns = useMemo(
    () => sourceTables.find((t) => t.tableName === sourceTable)?.columns ?? [],
    [sourceTables, sourceTable],
  );

  const targetFields = useMemo(() => {
    const col = targetCollections.find((c) => c.collectionName === targetCollection);
    if (!col) return [];
    return [...col.requiredFields, ...col.nullableFields];
  }, [targetCollections, targetCollection]);

  const canAdd = sourceTable && sourceColumn && targetCollection && targetField;

  const handleAdd = useCallback(() => {
    if (!canAdd) return;
    onAdd({
      sourceTable,
      sourceColumn,
      targetCollection,
      targetField,
      transform: transform || undefined,
    } as FieldMapping);
    setSourceTable('');
    setSourceColumn('');
    setTargetCollection('');
    setTargetField('');
    setTransform('');
  }, [canAdd, sourceTable, sourceColumn, targetCollection, targetField, transform, onAdd]);

  return (
    <tr className="bg-blue-50 border-t border-blue-200">
      <td className="px-2 py-1.5">
        <select
          value={sourceTable}
          onChange={(e) => { setSourceTable(e.target.value); setSourceColumn(''); }}
          className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
        >
          <option value="">Select table…</option>
          {sourceTables.map((t) => (
            <option key={t.tableName} value={t.tableName}>{t.tableName}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select
          value={sourceColumn}
          onChange={(e) => setSourceColumn(e.target.value)}
          disabled={!sourceTable}
          className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white disabled:opacity-40"
        >
          <option value="">Select column…</option>
          {sourceColumns.map((col) => (
            <option key={col} value={col}>{col}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select
          value={targetCollection}
          onChange={(e) => { setTargetCollection(e.target.value); setTargetField(''); }}
          className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
        >
          <option value="">Select collection…</option>
          {targetCollections.map((c) => (
            <option key={c.collectionName} value={c.collectionName}>{c.collectionName}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select
          value={targetField}
          onChange={(e) => setTargetField(e.target.value)}
          disabled={!targetCollection}
          className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white disabled:opacity-40"
        >
          <option value="">Select field…</option>
          {targetFields.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select
          value={transform}
          onChange={(e) => setTransform(e.target.value)}
          className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
        >
          {TRANSFORM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5 text-center">
        <button
          onClick={handleAdd}
          disabled={!canAdd}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 transition-colors disabled:opacity-40"
        >
          <Plus className="h-3 w-3" />
          Add
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Unmapped required fields warning banner
// ---------------------------------------------------------------------------

function UnmappedRequiredBanner({
  targetCollections,
  fieldMappings,
}: {
  targetCollections: TargetCollectionFields[];
  fieldMappings: FieldMapping[];
}) {
  const unmapped = useMemo(() => {
    const unmappedList: { collection: string; field: string }[] = [];
    for (const col of targetCollections) {
      for (const field of col.requiredFields) {
        const isMapped = fieldMappings.some(
          (m) => m.targetCollection === col.collectionName && m.targetField === field,
        );
        if (!isMapped) {
          unmappedList.push({ collection: col.collectionName, field });
        }
      }
    }
    return unmappedList;
  }, [targetCollections, fieldMappings]);

  if (unmapped.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2 mb-3">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
        All required fields are mapped.
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 mb-3">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <div>
        <span className="font-semibold">Unmapped required fields — cannot activate until resolved:</span>
        <ul className="mt-1 space-y-0.5 list-disc list-inside">
          {unmapped.map(({ collection, field }) => (
            <li key={`${collection}.${field}`}>
              <code>{collection}.{field}</code>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @component FieldMappingEditor
 * Purpose:     Editable (or read-only) table of FieldMapping[] for a dataset template.
 * Props:       FieldMappingEditorProps
 * Behavior:    Controlled via fieldMappings + onMappingsChange. Shows unmapped-required error.
 *              AI-suggested mappings are shown with a suggestion chip; user confirms or overrides.
 * Data Source: local (wizard/parent state)
 * Constraints: no direct API calls; no Dexie access
 * Performance: list is typically < 50 rows; no virtualization needed
 */
export function FieldMappingEditor({
  fieldMappings,
  onMappingsChange,
  sourceTables,
  targetCollections,
  suggestedMappings = [],
  readOnly = false,
}: FieldMappingEditorProps) {

  const handleRemove = useCallback((index: number) => {
    onMappingsChange(fieldMappings.filter((_, i) => i !== index));
  }, [fieldMappings, onMappingsChange]);

  const handleTransformChange = useCallback((index: number, transform: string) => {
    const validTransforms = ['string', 'number', 'boolean', 'date', 'trim', 'uppercase'] as const;
    type TransformType = FieldMapping['transform'];
    const coerced = (validTransforms as readonly string[]).includes(transform)
      ? (transform as TransformType)
      : undefined;
    const updated = fieldMappings.map((m, i) =>
      i === index ? { ...m, transform: coerced } : m,
    );
    onMappingsChange(updated);
  }, [fieldMappings, onMappingsChange]);

  const handleAdd = useCallback((mapping: FieldMapping) => {
    onMappingsChange([...fieldMappings, mapping]);
  }, [fieldMappings, onMappingsChange]);

  const handleApplySuggestion = useCallback((suggestion: SuggestedMapping) => {
    const alreadyMapped = fieldMappings.some(
      (m) =>
        m.sourceTable === suggestion.sourceTable &&
        m.sourceColumn === suggestion.sourceColumn,
    );
    if (alreadyMapped) return;
    onMappingsChange([
      ...fieldMappings,
      {
        sourceTable: suggestion.sourceTable,
        sourceColumn: suggestion.sourceColumn,
        targetCollection: suggestion.targetCollection,
        targetField: suggestion.targetField,
      } as FieldMapping,
    ]);
  }, [fieldMappings, onMappingsChange]);

  // Suggestions not yet applied
  const pendingSuggestions = useMemo(
    () =>
      suggestedMappings.filter(
        (s) =>
          !fieldMappings.some(
            (m) => m.sourceTable === s.sourceTable && m.sourceColumn === s.sourceColumn,
          ),
      ),
    [suggestedMappings, fieldMappings],
  );

  return (
    <div className="space-y-3">
      {/* Unmapped required fields banner */}
      {!readOnly && (
        <UnmappedRequiredBanner
          targetCollections={targetCollections}
          fieldMappings={fieldMappings}
        />
      )}

      {/* AI suggestions banner */}
      {!readOnly && pendingSuggestions.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-blue-700">
              AI Suggestions ({pendingSuggestions.length} pending)
            </span>
            <button
              onClick={() => pendingSuggestions.forEach(handleApplySuggestion)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Apply all
            </button>
          </div>
          <div className="space-y-1">
            {pendingSuggestions.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-xs">
                <span className="font-mono text-gray-700">
                  {s.sourceTable}.{s.sourceColumn}
                  <span className="mx-1 text-gray-400">→</span>
                  {s.targetCollection}.{s.targetField}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-gray-400">{s.confidence}</span>
                  <button
                    onClick={() => handleApplySuggestion(s)}
                    className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mapping table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left text-gray-500 font-medium w-1/4">Source Table</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium w-1/4">Source Column</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium w-1/4">Target Collection</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium w-1/4">Target Field</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium w-24">Transform</th>
              {!readOnly && <th className="px-3 py-2 w-10" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {fieldMappings.length === 0 && (
              <tr>
                <td
                  colSpan={readOnly ? 5 : 6}
                  className="px-3 py-6 text-center text-gray-400 italic"
                >
                  No field mappings defined.
                </td>
              </tr>
            )}
            {fieldMappings.map((m, i) => {
              const targetCol = targetCollections.find((c) => c.collectionName === m.targetCollection);
              const isRequiredField = targetCol?.requiredFields.includes(m.targetField) ?? false;
              return (
                <tr key={i} className={isRequiredField ? 'bg-white' : 'bg-gray-50/50'}>
                  <td className="px-3 py-1.5 font-mono text-gray-800">{m.sourceTable}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-800">
                    <div className="flex items-center gap-1.5">
                      {m.sourceColumn}
                      {isRequiredField && (
                        <span className="text-xs px-1 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">required</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-gray-800">{m.targetCollection}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-800">{m.targetField}</td>
                  <td className="px-3 py-1.5">
                    {readOnly ? (
                      <span className="text-gray-500">{m.transform ?? '—'}</span>
                    ) : (
                      <div className="relative">
                        <select
                          value={m.transform ?? ''}
                          onChange={(e) => handleTransformChange(i, e.target.value)}
                          className="w-full text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white appearance-none pr-5"
                        >
                          {TRANSFORM_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                      </div>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => handleRemove(i)}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                        aria-label="Remove mapping"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {/* Add row — only in edit mode */}
            {!readOnly && (
              <AddMappingRow
                sourceTables={sourceTables}
                targetCollections={targetCollections}
                onAdd={handleAdd}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
